import { TableUI } from "components/design/TableUI";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Header } from "components/DesignSystem/Header/Header";
import Slider from "components/DesignSystem/Slider/Slider";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { ReactNode, useMemo, useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  Row,
  useReactTable,
} from "react-table-8.10.7";
import {
  SensitiveDataFormField,
  SensitiveDataFormFieldOptionType,
  SensitiveDataType,
  SharedProfile,
} from "types/Models/sensitiveData";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import classNames from "classnames";
import Modal from "components/DesignSystem/Modal/Modal";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { SmallPlusIcon } from "components/icons/export const SmallPlusIcon";
import { Form, Formik, FormikHelpers, useFormikContext } from "formik";
import { useToast } from "hooks/useToast";
import { COPIED_TEXT, FAILED_COPIED_TEXT } from "constants/documents";
import ThreeDots from "static/images/ThreeDots.svg";
import {
  SensitiveDataResponse,
  useCreateSensitiveDataMutation,
  useDeleteSensitiveDataMutation,
  useEditSensitiveDataMutation,
  useGetAllSensitiveDataQuery,
  useGetAllSensitiveDataTypesQuery,
  useGetEmptyFormDataQuery,
  useGetSingleSensitiveDataQuery,
  useGrantAccessToSensitiveDataMutation,
  useRevokeAccessToSensitiveDataMutation,
  useSendChatCardMutation,
} from "apis/sensitiveData";
import {
  Route,
  Switch,
  useHistory,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { DeleteSensitiveDataModal } from "components/SensitiveData/DeleteSensitiveDataModal";
import { SensitiveDataBankField } from "components/SensitiveData/SensitiveDataBankField";
import { getManualBankInitialValues } from "util/getManualBankInitialValues";
import { useWithManualBankAccount } from "hooks/useWithManualBankAccount";
import { SensitiveDataPasswordField } from "components/SensitiveData/SensitiveDataPasswordField";
import { SensitiveDataSlider } from "components/SensitiveData/SensitiveDataSlider";
import { SensitiveDataEmptyState } from "components/SensitiveData/SensitiveDataEmptyState";
import { SensitiveDataErrorState } from "components/SensitiveData/SensitiveDataErrorState";
import { SensitiveDataItemCell } from "components/SensitiveData/SensitiveDataItemCell";
import { SensitiveDataLastUpdatedCell } from "components/SensitiveData/SensitiveDataLastUpdatedCell";
import { SensitiveDataValueCell } from "components/SensitiveData/SensitiveDataValueCell";
import {
  getSensitiveDataSelectOptions,
  SensitiveDataSelectOption,
} from "util/getSensitiveDataSelectOptions";
import {
  SENSITIVE_DATA_MODAL_TITLE_BY_TYPE,
  SENSITIVE_DATA_BANK_FIELD_LABEL,
  SENSITIVE_DATA_BANK_AUTOFILL_KEY,
  SENSITIVE_DATA_BANK_ENTRY_MODE,
  SENSITIVE_DATA_BANK_ENTRY_MODE_KEY,
  SENSITIVE_DATA_KEEP_VALUE_HINT,
  SENSITIVE_DATA_PASSWORD_FIELD_KEY,
  SENSITIVE_DATA_TYPE_CREDENTIALS,
} from "constants/sensitiveData";
import { useGetCountryStatesQuery } from "store/apis/teamSetting";
import { US_UUID } from "constants/regAgent";
import { isSensitiveDataFieldVisible } from "util/isSensitiveDataFieldVisible";
import {
  buildSensitiveDataPayload,
  SensitiveDataFormValues,
} from "util/buildSensitiveDataPayload";
import { getSensitiveDataEditableFields } from "util/getSensitiveDataEditableFields";
import { getSensitiveDataInitialValues } from "util/getSensitiveDataInitialValues";
import { useSensitiveDataSecretValues } from "hooks/useSensitiveDataSecretValues";
import { SwitchField } from "components/DesignSystem/Switch/SwitchField";
import { Switch as ToggleSwitch } from "components/DesignSystem/Switch/Switch";
import ReactCountryFlag from "react-country-flag";
import { BackendError } from "types/utils/error";
import { createDynamicValidationSchema } from "formValidations/sensitiveDataForm";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useUploadFileMutation } from "store/apis/documents";
import { FieldBuilder } from "components/Entity/EntityFormEngine/FieldBuilder";
import { FilePreviewTag } from "components/FilePreviewTag/FilePreviewTag";
import { useGetAllCpaTeamsQuery } from "store/apis/serviceTeam";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import { HeaderActions } from "components/AppLayout/HeaderActions";
import { SelectAutofillCombobox } from "components/SelectAutofillCombobox";
import { SensitiveDataFieldError } from "components/SensitiveData/SensitiveDataFieldError";
import { useGetAutofillQuery } from "store/apis/autofill";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { BankAutofill } from "types/Models/bankAutofill";
import { PersonaAutofill } from "types/Models/personaAutofill";
import { AddressAutofill } from "types/Models/addressAutofill";
import { DirectorAutofill } from "types/Models/directorAutofill";

const RowAction = ({ rowData }: { rowData: SensitiveDataResponse }) => {
  const deleteModal = useModal();
  const editModal = useModal();

  return (
    <div
      className="t-text-subtext t-text-text-30"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="t-flex t-justify-center t-items-center t-h-full">
        <Dropdown.Root>
          <Dropdown.Trigger asChild>
            <div>
              <Button size="xsmall" customType="ghost_icon">
                <img src={ThreeDots} alt="Action" className="t-select-none" />
              </Button>
            </div>
          </Dropdown.Trigger>
          <Dropdown.Portal>
            <Dropdown.Content sideOffset={8} side="bottom" className="t-mr-16">
              <Dropdown.Item onSelect={editModal.open}>Edit</Dropdown.Item>
              <Dropdown.Item onSelect={deleteModal.open} type="danger">
                Delete
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown.Portal>
        </Dropdown.Root>
      </div>

      <EditSensitiveDataDynamicForm
        close={editModal.close}
        isOpen={editModal.isOpen}
        formId={rowData.uuid}
      />

      <DeleteSensitiveDataModal
        close={deleteModal.close}
        isOpen={deleteModal.isOpen}
        sensitiveDataId={rowData.uuid}
      />
    </div>
  );
};

export const SensitiveDataRequestModal = ({
  formId,
  isOpen,
  close,
  channelUrl,
  entityId,
}: {
  formId: string;
  isOpen: boolean;
  close: () => void;
  channelUrl: string;
  entityId: string;
}) => {
  const [sendChatCard, { isLoading: isSendingChatCard }] =
    useSendChatCardMutation();
  const [createSensitiveData, { isLoading: isRequesting }] =
    useCreateSensitiveDataMutation();
  const { alertToast, successToast } = useToast();
  const createRequest = async (values_obj: any) => {
    const payload = {
      sensitive_data_type_id: formId,
      form_data: Object.keys(values_obj).map((key) => ({
        field_key: key,
        field_value: values_obj[key],
      })),
    };
    try {
      if (entityId) {
        const response = await createSensitiveData({
          entityId,
          payload,
        }).unwrap();

        const { uuid: sensitiveDataInstanceId } = response || {};

        await sendChatCard({
          entityId,
          sensitiveDataInstanceId,
          channelUrl,
        }).unwrap();
        successToast({ message: "Request created successfully" });
        close();
      }
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as {}
      );
    }
  };
  const {
    data: formFields = [],
    isLoading,
    isSuccess,
  } = useGetEmptyFormDataQuery(
    {
      entityId,
      formId,
    },
    { skip: !entityId || !formId }
  );
  const commonFormFields = formFields.filter(
    (field) => field.is_common_attribute == true
  );
  const isEmpty = commonFormFields.length == 0;
  return (
    <Formik
      initialValues={{
        name: "",
        description: "",
      }}
      onSubmit={createRequest}
    >
      {({ submitForm }) => (
        <Form>
          <Modal.Root open={isOpen} onOpenChange={close}>
            <Modal.Content>
              <Async.Root {...{ isLoading, isSuccess, isEmpty }}>
                <Async.Empty>-</Async.Empty>
                <Async.Success>
                  <Modal.Header>
                    <Modal.Title>Request</Modal.Title>
                    <Modal.Close />
                  </Modal.Header>

                  <Modal.Body className="t-px-5 t-py-8 ">
                    <div className="t-flex t-flex-col t-gap-4">
                      {commonFormFields.map((formField, index) => {
                        return (
                          <DynamicInputFields
                            formField={formField}
                            key={formField.uuid}
                            index={index}
                          />
                        );
                      })}
                    </div>
                  </Modal.Body>
                  <Modal.FooterButtonGroup>
                    <Button customType="secondary" onClick={close}>
                      Cancel
                    </Button>
                    <Button
                      customType="primary"
                      onClick={submitForm}
                      isLoading={isRequesting || isSendingChatCard}
                    >
                      Send Request
                    </Button>
                  </Modal.FooterButtonGroup>
                </Async.Success>
              </Async.Root>
            </Modal.Content>
          </Modal.Root>
        </Form>
      )}
    </Formik>
  );
};

const columnHelper = createColumnHelper<SensitiveDataResponse>();

const EMPTY_ROWS: SensitiveDataResponse[] = [];

const SENSITIVE_DATA_COLUMNS = [
  columnHelper.display({
    id: "item",
    size: 40,
    header: "Item",
    cell: ({ row }) => <SensitiveDataItemCell record={row.original} />,
  }),

  columnHelper.display({
    id: "lastUpdated",
    size: 30,
    header: "Last updated",
    cell: ({ row }) => <SensitiveDataLastUpdatedCell record={row.original} />,
  }),

  columnHelper.display({
    id: "value",
    size: 20,
    header: "Value",
    cell: ({ row }) => <SensitiveDataValueCell record={row.original} />,
  }),

  columnHelper.display({
    id: "action",
    size: 10,
    cell: (info) => {
      const {
        row: { original },
      } = info;
      const { has_delete_access, has_edit_access } = original || {};

      return has_edit_access && has_delete_access ? (
        <RowAction rowData={original} />
      ) : (
        <></>
      );
    },
  }),
];

const SensitiveDataTable = ({ data }: { data: SensitiveDataResponse[] }) => {
  const { url, path } = useRouteMatch();
  const history = useHistory();

  const table = useReactTable({
    data: data || EMPTY_ROWS,
    columns: SENSITIVE_DATA_COLUMNS,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      size: 10,
      minSize: 1,
      maxSize: 100,
    },
  });

  const onRowClick = (row: Row<SensitiveDataType>) => {
    history.push(`${url}/${row.original.uuid}`);
  };

  return (
    <>
      <TableUI
        size="regular"
        table={table}
        onRowClick={onRowClick}
        isHeaderCellUpperCase={false}
      />
      <Switch>
        <Route path={`${path}/:sensitiveDataId`}>
          <SensitiveDataSlider onClose={() => history.push(url)} />
        </Route>
      </Switch>
    </>
  );
};

const DynamicSelectFieldLabel = ({
  option,
}: {
  option: SensitiveDataSelectOption;
}) => (
  <div className="t-flex t-gap-2">
    {Boolean(option.codeAlpha2) && (
      <ReactCountryFlag
        countryCode={option.codeAlpha2!}
        svg
        title={option.label}
        style={{ width: "1.5em", height: "1.5em" }}
        className="t-rounded"
      />
    )}
    {option.label}
  </div>
);
const PERSONA_LINKED_FIELDS = [
  "persona",
  "country",
  "tax_id",
  "name_of_the_person",
];

const DynamicInputFields = ({
  formField,
  index,
  entityId: entityIdFromProps,
  groupId: groupIdFromProps,
  isEditing = false,
}: {
  formField: SensitiveDataFormField;
  index: number;
  entityId?: string;
  groupId?: string;
  isEditing?: boolean;
}) => {
  const {
    data_type,
    field_key,
    key,
    placeholder,
    value,
    is_required,
    is_encrypted,
    uuid,
    options,
  } = formField || {};
  const { unsorted_folder_id, uuid: groupId } = useCurrentGroupContext();
  const [uploadFile, { isLoading }] = useUploadFileMutation();
  const { setFieldValue, values } = useFormikContext<Record<string, string>>();

  const isVisible = isSensitiveDataFieldVisible(formField, values);

  const { entityId } = useParams<{ entityId: string }>();
  const finalEntityId = entityId || entityIdFromProps;
  const finalGroupId = groupId || groupIdFromProps;
  const { data: countries } = useGetCountriesDataQuery();

  const clearPersona = () => {
    PERSONA_LINKED_FIELDS.forEach((linkedField) =>
      setFieldValue(linkedField, "")
    );
  };

  const applyPersona = (persona: PersonaAutofill) => {
    setFieldValue("persona", persona.uuid || "");

    const selectedCountry = countries?.find(
      ({ name }) => name === persona.citizenship
    );

    setFieldValue("country", selectedCountry?.name || "");
    setFieldValue("tax_id", persona.tax_id || "");
    setFieldValue(
      "name_of_the_person",
      `${persona.first_name} ${persona.last_name}`.trim()
    );
  };

  const handlePersonaChange = (personaUuid: string) => {
    if (data_type !== "PERSONA") return;

    if (!personaUuid) {
      clearPersona();
      return;
    }

    setFieldValue("persona", personaUuid);

    const personaList = options as (PersonaAutofill &
      BankAutofill &
      AddressAutofill &
      DirectorAutofill)[];

    const selectedPersona = personaList.find(
      ({ uuid }) => uuid === personaUuid
    );

    if (selectedPersona) applyPersona(selectedPersona);
  };

  const handlePersonaAdded = (
    autofill:
      | BankAutofill
      | AddressAutofill
      | PersonaAutofill
      | DirectorAutofill
  ) => {
    if (data_type !== "PERSONA") return;
    if (!("first_name" in autofill)) return;

    applyPersona(autofill);
  };

  const selectOptions = getSensitiveDataSelectOptions(options);
  const currentOption = selectOptions.find(
    ({ value }) => value === values[field_key]
  );
  const selectedValue = currentOption
    ? {
        label: <DynamicSelectFieldLabel option={currentOption} />,
        value: currentOption.value,
      }
    : null;

  const entityOptions = selectOptions;
  const currentEntityId = values[field_key];
  const selectedEntityOption =
    entityOptions.find((option) => option.value === currentEntityId) || null;

  if (!isVisible) return null;

  let fieldPlaceholder = placeholder;

  if (isEditing && is_encrypted && !value) {
    fieldPlaceholder = SENSITIVE_DATA_KEEP_VALUE_HINT;
  }

  if (field_key === SENSITIVE_DATA_PASSWORD_FIELD_KEY) {
    return (
      <SensitiveDataPasswordField
        name={field_key}
        label={key}
        value={values[field_key]}
        placeholder={fieldPlaceholder}
        required={is_required}
      />
    );
  }

  switch (data_type) {
    case "PERSONA":
      return (
        <div className="t-flex t-flex-col t-gap-1.5">
          <SelectAutofillCombobox
            placeholder={fieldPlaceholder}
            entityId={finalEntityId!}
            groupId={finalGroupId!}
            type="authorized_persons"
            name="persona"
            selected={values.persona as string}
            label={key}
            required={is_required}
            onChange={handlePersonaChange}
            onSelect={handlePersonaAdded}
          />
          <SensitiveDataFieldError name="persona" />
        </div>
      );
    case "SELECT":
      return (
        <Combobox
          label={key}
          withForm
          placeholder={placeholder}
          menuPortalTarget={document.body}
          options={selectOptions.map((option) => ({
            label: <DynamicSelectFieldLabel option={option} />,
            value: option.value,
          }))}
          value={selectedValue}
          isSearchable={true}
          name={field_key}
          required={is_required}
        />
      );
    case "BOOLEAN":
      return (
        <SwitchField
          label={key}
          name={field_key}
          switchSide="left"
          size="small"
        />
      );
    case "BANK_ACCOUNT":
      return (
        <SensitiveDataBankField
          name={field_key}
          label={SENSITIVE_DATA_BANK_FIELD_LABEL}
          placeholder={placeholder}
          required={is_required}
          entityId={finalEntityId!}
          groupId={finalGroupId!}
          isEditingLinkedBank={
            isEditing && typeof value === "string" && Boolean(value)
          }
        />
      );
    case "ENTITY":
      return (
        <Combobox
          label={key}
          withForm
          placeholder={placeholder}
          menuPortalTarget={document.body}
          options={entityOptions}
          value={selectedEntityOption}
          isSearchable={true}
          name={field_key}
          required={is_required}
        />
      );

    default:
      return (
        <FieldBuilder
          field={{
            label: key,
            name: field_key,
            value_regex: "",
            placeholder: fieldPlaceholder,
            type: data_type === "STRING" ? "INPUT" : data_type,
            is_delete_allowed: false,
            can_copy: false,
            options: [],
            formation_type: "",
            uuid,
            tooltip: "",
            disabled: false,
            value: value,
            show: null,
            is_required: is_required,
            is_onboarding_field: false,
            is_only_onboarding_field: false,
            section_name: null,
          }}
          size="regular"
        />
      );
  }
};

export const EditSensitiveDataDynamicForm = ({
  formId,
  isOpen,
  close,
  entityId: entityIdFromProps,
  groupId: groupIdFromProps,
}: {
  formId: string;
  isOpen: boolean;
  close: () => void;
  entityId?: string;
  groupId?: string;
}) => {
  const { entityId } = useParams<{ entityId: string }>();
  const finalEntityId = entityId || entityIdFromProps;
  const { uuid: currentGroupId } = useCurrentGroupContext();
  const finalGroupId = groupIdFromProps || currentGroupId;
  const { data, isLoading, isSuccess, originalArgs } =
    useGetSingleSensitiveDataQuery(
      {
        entityId: finalEntityId!,
        formId,
      },
      {
        skip: !formId || !finalEntityId || !isOpen,
      }
    );

  const [editSensitiveData, { isLoading: isUpdating }] =
    useEditSensitiveDataMutation();

  const { form_data: recordFields = [], sensitive_data_type: formType = "" } =
    data || {};
  const isEmpty = recordFields?.length == 0;

  const formFields = useMemo(
    () => getSensitiveDataEditableFields(recordFields),
    [recordFields]
  );
  const { alertToast, successToast } = useToast();

  const editTitle = SENSITIVE_DATA_MODAL_TITLE_BY_TYPE[formType] || formType;

  const { secretByFieldKey, isLoading: isLoadingSecrets } =
    useSensitiveDataSecretValues({
      entityId: finalEntityId,
      formId,
      fields: formFields,
      skip: !isOpen,
    });

  const bankField = formFields.find(
    ({ data_type }) => data_type === "BANK_ACCOUNT"
  );
  const linkedBankId =
    typeof bankField?.value === "string" ? bankField.value : "";

  const { data: bankAccounts = [] } = useGetAutofillQuery(
    {
      groupId: finalGroupId!,
      entityId: finalEntityId!,
      autofillKey: SENSITIVE_DATA_BANK_AUTOFILL_KEY,
    },
    { skip: !linkedBankId || !finalGroupId || !finalEntityId }
  );

  const linkedBank = (bankAccounts as BankAutofill[]).find(
    ({ uuid }) => uuid === linkedBankId
  );

  const initialValues = {
    ...getSensitiveDataInitialValues(
      formFields,
      getManualBankInitialValues(linkedBank)
    ),
    ...secretByFieldKey,
    ...(linkedBankId && {
      [SENSITIVE_DATA_BANK_ENTRY_MODE_KEY]:
        SENSITIVE_DATA_BANK_ENTRY_MODE.MANUAL,
    }),
  };

  const { withManualBankAccount, isSavingBankAccount } =
    useWithManualBankAccount({
      fields: formFields,
      entityId: finalEntityId!,
      groupId: finalGroupId!,
    });

  const handleSubmit = async (values_obj: SensitiveDataFormValues) => {
    try {
      const values = await withManualBankAccount(values_obj);

      const payload = {
        form_data: buildSensitiveDataPayload(formFields, values),
      };

      await editSensitiveData({
        entityId: finalEntityId!,
        formId,
        payload,
      }).unwrap();
      successToast({ message: "Sensitive data edited successfully" });
      close();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError).data?.error?.message ??
          "Could not save the changes",
      });
    }
  };

  const validationSchema = createDynamicValidationSchema(formFields);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ submitForm, dirty, isValid }) => (
        <Form>
          <Modal.Root open={isOpen} onOpenChange={close}>
            <Modal.Content useCustomOverlay>
              <Async.Root
                {...{
                  isLoading: isLoading || isLoadingSecrets,
                  isSuccess,
                  isEmpty,
                }}
              >
                <Async.Empty>-</Async.Empty>
                <Async.Success>
                  <Modal.Header>
                    <Modal.Title>{`Edit ${editTitle}`}</Modal.Title>
                    <Modal.Close />
                  </Modal.Header>
                  <Modal.Body className="t-flex t-flex-col t-px-8 t-py-5 t-gap-4">
                    {formFields.map((formField, index) => {
                      return (
                        <DynamicInputFields
                          formField={formField}
                          key={formField.uuid}
                          index={index}
                          entityId={finalEntityId}
                          groupId={groupIdFromProps}
                          isEditing
                        />
                      );
                    })}
                  </Modal.Body>
                  <Modal.FooterButtonGroup>
                    <Modal.RawClose asChild>
                      <Button>Cancel</Button>
                    </Modal.RawClose>
                    <Button
                      customType="primary"
                      onClick={submitForm}
                      isLoading={isUpdating || isSavingBankAccount}
                      disabled={
                        !dirty || !isValid || isUpdating || isSavingBankAccount
                      }
                    >
                      Save
                    </Button>
                  </Modal.FooterButtonGroup>
                </Async.Success>
              </Async.Root>
            </Modal.Content>
          </Modal.Root>
        </Form>
      )}
    </Formik>
  );
};
export const SensitiveDataDynamicForm = ({
  formId,
  formName,
  isOpen,
  close,
  entityId: entityIdFromProps,
  countryStateId,
}: {
  formId: string;

  formName: string;
  isOpen: boolean;
  close: () => void;
  entityId?: string;
  countryStateId?: string;
}) => {
  const { entityId: entityIdFromRoute } = useParams<{ entityId: string }>();
  const entityId = entityIdFromRoute || entityIdFromProps || "";
  const {
    data: formFields = [],
    isLoading,
    isSuccess,
  } = useGetEmptyFormDataQuery(
    {
      entityId,
      formId,
    },
    {
      skip: !formId || !entityId,
      refetchOnMountOrArgChange: true,
    }
  );
  const isEmpty = formFields?.length == 0;
  const { alertToast, successToast } = useToast();
  const initialValues = getSensitiveDataInitialValues(formFields);
  const { uuid: currentGroupId } = useCurrentGroupContext();

  const { withManualBankAccount, isSavingBankAccount } =
    useWithManualBankAccount({
      fields: formFields,
      entityId,
      groupId: currentGroupId,
    });

  const addTitle = SENSITIVE_DATA_MODAL_TITLE_BY_TYPE[formName] || formName;
  const [createSensitiveData, { isLoading: isCreating }] =
    useCreateSensitiveDataMutation();

  const isStateScopableType = formName === SENSITIVE_DATA_TYPE_CREDENTIALS;
  const isStatePreset = Boolean(countryStateId);
  const [isStateSpecific, setIsStateSpecific] = useState(isStatePreset);
  const [selectedStateId, setSelectedStateId] = useState<string | null>(null);
  const showStatePicker =
    isStateScopableType && (isStatePreset || isStateSpecific);
  const { data: states = [] } = useGetCountryStatesQuery(
    { countryId: US_UUID },
    { skip: !showStatePicker }
  );
  const finalCountryStateId = countryStateId ?? selectedStateId ?? undefined;

  const handleStateSpecificToggle = (checked: boolean) => {
    setIsStateSpecific(checked);
    if (!checked) setSelectedStateId(null);
  };

  const lastFieldIndex = formFields.length - 1;
  const fieldsBeforeStateToggle = formFields.slice(0, lastFieldIndex);
  const fieldsAfterStateToggle = formFields.slice(lastFieldIndex);

  const handleSubmit = async (values_obj: SensitiveDataFormValues) => {
    try {
      const values = await withManualBankAccount(values_obj);

      const payload = {
        sensitive_data_type_id: formId,
        form_data: buildSensitiveDataPayload(formFields, values),
        ...(finalCountryStateId && { country_state_id: finalCountryStateId }),
      };

      await createSensitiveData({
        entityId,
        payload,
      }).unwrap();
      successToast({ message: "Credentials stored successfully" });

      close();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };
  const validationSchema = createDynamicValidationSchema(formFields);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(v) => {
        handleSubmit(v);
      }}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ submitForm, dirty, isValid }) => {
        return (
          <Form>
            <Modal.Root open={isOpen} onOpenChange={close}>
              <Modal.Content useCustomOverlay>
                <Modal.Header>
                  <Modal.Title>{`Add ${addTitle}`}</Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body className="t-flex t-flex-col t-px-8 t-py-5 t-gap-4">
                  <Async.Root {...{ isLoading, isSuccess, isEmpty }}>
                    <Async.Empty>-</Async.Empty>
                    <Async.Success>
                      {fieldsBeforeStateToggle.map((formField, index) => (
                        <DynamicInputFields
                          formField={formField}
                          key={formField.uuid}
                          index={index}
                          entityId={entityId}
                        />
                      ))}

                      {isStateScopableType && !isStatePreset && (
                        <div className="t-flex t-items-center t-gap-2">
                          <ToggleSwitch
                            size="small"
                            checked={isStateSpecific}
                            onCheckedChange={handleStateSpecificToggle}
                          />
                          <span className="t-text-body-sm t-text-text-60">
                            This credential is specific to a state
                          </span>
                        </div>
                      )}
                      {showStatePicker && (
                        <div className="t-flex t-flex-col t-gap-1">
                          <Label>State</Label>
                          <Combobox
                            placeholder="Select a state"
                            isDisabled={isStatePreset}
                            options={states.map((s) => ({
                              label: s.name,
                              value: s.state_id,
                            }))}
                            value={
                              finalCountryStateId
                                ? {
                                    label:
                                      states.find(
                                        (s) =>
                                          s.state_id === finalCountryStateId
                                      )?.name ?? "",
                                    value: finalCountryStateId,
                                  }
                                : null
                            }
                            onChange={(option) =>
                              setSelectedStateId(
                                (option as { value: string } | null)?.value ??
                                  null
                              )
                            }
                          />
                        </div>
                      )}

                      {fieldsAfterStateToggle.map((formField, index) => (
                        <DynamicInputFields
                          formField={formField}
                          key={formField.uuid}
                          index={fieldsBeforeStateToggle.length + index}
                          entityId={entityId}
                        />
                      ))}
                    </Async.Success>
                  </Async.Root>
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button>Cancel</Button>
                  </Modal.RawClose>
                  <Button
                    customType="primary"
                    onClick={submitForm}
                    isLoading={isCreating || isSavingBankAccount}
                    disabled={
                      !dirty ||
                      !isValid ||
                      isCreating ||
                      isSavingBankAccount ||
                      (showStatePicker && !finalCountryStateId)
                    }
                  >
                    Submit
                  </Button>
                </Modal.FooterButtonGroup>
              </Modal.Content>
            </Modal.Root>
          </Form>
        );
      }}
    </Formik>
  );
};
export const AddSensitiveDataDropdown = () => {
  const { data: formTypes } = useGetAllSensitiveDataTypesQuery();
  const [formId, setFormId] = useState<string>("");
  const [formName, setFormName] = useState<string>("");
  const {
    isOpen: isCreateModalOpen,
    open: openCreateModal,
    close: closeCreateModal,
  } = useModal();
  return (
    <div>
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <Button customType="primary" size="small">
            <div className="t-flex t-items-center t-gap-1">
              <SmallPlusIcon />
              Add sensitive data
            </div>
          </Button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content
            align="start"
            sideOffset={8}
            className="t-min-w-[176px]"
            asChild
          >
            {formTypes?.map(({ uuid, name }, index) => {
              return (
                <Dropdown.Item
                  key={index}
                  textValue={name}
                  onSelect={() => {
                    setFormId(uuid);
                    setFormName(name);
                    openCreateModal();
                  }}
                >
                  {name}
                </Dropdown.Item>
              );
            })}
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      {isCreateModalOpen && (
        <SensitiveDataDynamicForm
          formId={formId}
          formName={formName}
          isOpen={isCreateModalOpen}
          close={closeCreateModal}
        />
      )}
    </div>
  );
};
export const SensitiveData = () => {
  const { entityId } = useParams<{ entityId: string }>();
  const { isAdmin, isCustomer } = useRoleBasedView();
  const {
    data = [],
    isLoading,
    isSuccess,
    isError,
    isUninitialized,
    error,
  } = useGetAllSensitiveDataQuery({ entityId });

  const isEmpty = data.length === 0;
  return (
    <div>
      {(isCustomer || isAdmin) && (
        <HeaderActions>
          <AddSensitiveDataDropdown />
        </HeaderActions>
      )}
      <Async.Root
        {...{
          isLoading: isLoading || isUninitialized,
          isEmpty,
          isSuccess,
          isError,
        }}
      >
        <Async.Empty>
          <SensitiveDataEmptyState />
        </Async.Empty>
        <Async.ErrorHandler>
          <SensitiveDataErrorState error={error} />
        </Async.ErrorHandler>
        <Async.Success>
          <SensitiveDataTable data={data} />
        </Async.Success>
      </Async.Root>
    </div>
  );
};
